import type { ReactNode } from 'react';
import {
  CheckCircle,
  Info,
  Warning,
  WarningCircle,
} from '@phosphor-icons/react';
import { useToast } from '../../hooks/use-toast';
import {
  Toast,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from './toast';

const TOAST_ICON_BY_VARIANT = {
  default: Info,
  info: Info,
  success: CheckCircle,
  warning: Warning,
  destructive: WarningCircle,
} as const;

const TOAST_ICON_CLASS_BY_VARIANT = {
  default: 'text-neutral-300',
  info: 'text-neutral-300',
  success: 'text-success',
  warning: 'text-warning',
  destructive: 'text-error',
} as const;

const getToastMessage = (
  title?: ReactNode,
  description?: ReactNode
): ReactNode => {
  if (title != null && title !== '') {
    return title;
  }

  return description;
};

export function Toaster() {
  const { toasts } = useToast();

  return (
    <ToastProvider duration={2600}>
      {toasts.map(({ id, title, description, action, variant, ...props }) => {
        const resolvedVariant = variant ?? 'default';
        const Icon = TOAST_ICON_BY_VARIANT[resolvedVariant];
        const message = getToastMessage(title, description);

        return (
          <Toast key={id} variant={resolvedVariant} duration={2600} {...props}>
            <Icon
              className={`size-4 shrink-0 ${TOAST_ICON_CLASS_BY_VARIANT[resolvedVariant]}`}
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              {title && description && title !== description ? (
                <>
                  <ToastTitle className="text-xs font-semibold leading-snug text-white">
                    {title}
                  </ToastTitle>
                  <ToastDescription className="text-xs font-normal leading-snug text-neutral-300 opacity-100">
                    {description}
                  </ToastDescription>
                </>
              ) : (
                <>
                  {message ? (
                    <ToastDescription className="text-xs font-medium leading-snug text-white opacity-100">
                      {message}
                    </ToastDescription>
                  ) : null}
                  <ToastTitle className="sr-only">{message}</ToastTitle>
                </>
              )}
              {action}
            </div>
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}
